import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Building2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { STATS, resolveImage } from "../../lib/data";
import { useSettings } from "../../lib/useSettings";
import { useStats } from "../../lib/useStats";
import { hasSeenSplash } from "../BrandSplash";
import SplitText from "../SplitText";
import HeroVideoBackground from "../HeroVideoBackground";

const DEFAULT_HERO =
  "https://images.pexels.com/photos/28950670/pexels-photo-28950670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2400";

// When splash is shown, hero entrance starts after splash fade (1.05s).
// On subsequent visits, splash is skipped → tiny delay only.
const ENTRY_BASE_DELAY = hasSeenSplash() ? 0.1 : 1.05;
const EASE_OUT_EXPO = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const settings = useSettings();
  const stats = useStats();
  const heroImg = settings?.hero_home || DEFAULT_HERO;
  // Live stats with safe fallback to legacy STATS constants
  const hotelsCount = stats?.hotels ?? STATS.hotels;
  const roomsCount = stats?.rooms ?? STATS.rooms;
  const pipelineHotels = stats?.pipeline_hotels ?? STATS.pipelineHotels;
  const pipelineRooms = stats?.pipeline_rooms ?? STATS.pipelineRooms;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden"
    >
      {/* background layer — video (with poster fallback) OR image */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0"
      >
        {settings?.hero_video_enabled &&
        (settings?.hero_video_url_mp4 || settings?.hero_video_url_webm) ? (
          <HeroVideoBackground
            src={settings.hero_video_url_mp4}
            srcWebm={settings.hero_video_url_webm}
            poster={settings.hero_video_poster || heroImg}
            objectFit={settings.hero_video_object_fit || "cover"}
            playOnMobile={!!settings.hero_video_play_on_mobile}
            overlayClassName=""
            data-testid="home-hero-video"
          />
        ) : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url("${resolveImage(heroImg)}")`,
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-[#08080A]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.7)_100%)]" />
      </motion.div>

      {/* grain overlay */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* floating glass stat cards */}
      <motion.div
        style={{ opacity }}
        className="absolute top-24 right-6 md:right-12 z-20 hidden md:block"
      >
        <div className="glass rounded-2xl p-5 w-[260px] animate-float">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50">
            <Sparkles size={12} className="text-[#C48F56]" />
            Portafoglio 2026
          </div>
          <div className="mt-3 flex items-end gap-2">
            <span data-testid="kpi-hotels" className="font-display text-5xl leading-none">
              {hotelsCount}
            </span>
            <span className="text-white/50 text-sm mb-1">hotel operativi</span>
          </div>
          <div className="mt-1 text-xs text-white/50 font-mono">
            + {pipelineHotels} in pipeline
          </div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-28 left-6 md:left-12 z-20 hidden md:block"
      >
        <div className="glass rounded-2xl p-5 w-[260px] animate-float" style={{ animationDelay: "-3s" }}>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50">
            <Building2 size={12} className="text-[#C48F56]" />
            Camere totali
          </div>
          <div className="mt-3 flex items-end gap-2">
            <span data-testid="kpi-rooms" className="font-display text-5xl leading-none">
              {Number(roomsCount).toLocaleString("it-IT")}
            </span>
          </div>
          <div className="mt-1 text-xs text-white/50 font-mono">
            + {pipelineRooms} camere in costruzione
          </div>
          <div
            data-testid="kpi-target-2027"
            className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between gap-2"
          >
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#C48F56] font-mono">
              Target 2027
            </span>
            <span className="font-display text-2xl text-white leading-none">
              {Number((Number(roomsCount) || 0) + (Number(pipelineRooms) || 0)).toLocaleString("it-IT")}
            </span>
          </div>
        </div>
      </motion.div>

      {/* title */}
      <motion.div
        style={{ y: titleY, opacity }}
        className="relative z-10 h-full flex flex-col justify-end pb-24 md:pb-28 px-6 md:px-12 max-w-[1440px] mx-auto"
      >
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                delayChildren: ENTRY_BASE_DELAY,
                staggerChildren: 0.12,
              },
            },
          }}
        >
          <motion.div
            variants={{
              hidden: { opacity: 0, x: -16 },
              show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: EASE_OUT_EXPO } },
            }}
            className="flex items-center gap-3 text-[11px] md:text-xs uppercase tracking-[0.3em] text-white/60"
          >
            <span className="h-px w-10 bg-[#C48F56]" />
            Milano · dal {STATS.foundedYear}
          </motion.div>
          <SplitText
            as="h1"
            className="mt-6 font-display text-balance tracking-tight text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium"
            text={"Dal progetto\n*all'accoglienza.*"}
            delay={ENTRY_BASE_DELAY + 0.1}
            duration={0.9}
            stagger={0.018}
            data-testid="home-hero-title"
          />
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 16 },
              show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE_OUT_EXPO } },
            }}
            className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <p className="text-white/70 max-w-xl text-lg md:text-xl leading-relaxed">
              Sviluppiamo, costruiamo e gestiamo hotel nella città di Milano.
              Una realtà italiana indipendente che segue ogni progetto dall&apos;idea
              iniziale alla gestione operativa.
            </p>
            <div className="flex items-center gap-3">
              <Link
                to="/hotel"
                data-testid="hero-cta-hotels"
                className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-[#C48F56] transition-colors"
              >
                Portafoglio hotel
              </Link>
              <Link
                to="/chi-siamo"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm hover:bg-white/10 transition-colors"
              >
                Il nostro modello
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* bottom scroll indicator */}
      <motion.div
        style={{ opacity }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40"
      >
        <ArrowDown size={14} className="animate-bounce" />
        <span className="text-[10px] uppercase tracking-[0.3em]">Scorri</span>
      </motion.div>
    </section>
  );
}
